import React, { useState, useEffect } from "react";
import { Box, TextField, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { saveSetupData, getSetupData } from "../../utils/setupStorage";
import CountrySelect, {
  normalizeCountryStorageValueToIso,
} from "../CountrySelect";
import AddressStreetAutocomplete from "../address/AddressStreetAutocomplete";
import PhoneNumberInput from "../PhoneNumberInput";
import { validateEmail, validateStreetWithHouseNumber } from "../../utils";

interface AddressFormData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  country: string;
  email: string;
  phoneNumber: string;
}

export interface OrganizationAddressStepProps {
  onNext: () => void;
  onBack: () => void;
  /** localStorage step key to read/write the address under (see setupStorage). Defaults to 'organizationAddress'. */
  localStorageKey?: string;
  /** i18n key for the step heading. Defaults to 'OrganizationAddress'. */
  titleKey?: string;
  /** i18n key for the step description. Defaults to 'SetupAddressDescription'. */
  descriptionKey?: string;
}

/**
 * Address form step shared across setup wizards (backoffice's headquarters step,
 * reseller's headquarters + customer-contact steps). No hardcoded text; every
 * caller's own de.json/en.json defines its own wording for these flat keys.
 */
const OrganizationAddressStep: React.FC<OrganizationAddressStepProps> = ({
  onNext,
  onBack,
  localStorageKey = "organizationAddress",
  titleKey = "OrganizationAddress",
  descriptionKey = "SetupAddressDescription",
}) => {
  const { t, i18n } = useTranslation();
  const [organizationName, setOrganizationName] = useState<string>("");

  const [formData, setFormData] = useState<AddressFormData>({
    addressLine1: "",
    addressLine2: "",
    city: "",
    zipCode: "",
    country: "DE",
    email: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load organization name and existing address data from localStorage
  useEffect(() => {
    const orgData = getSetupData<{ name?: string }>("organization");
    if (orgData?.name) {
      setOrganizationName(orgData.name);
    }

    const savedAddress =
      getSetupData<Partial<AddressFormData>>(localStorageKey);
    if (savedAddress) {
      setFormData({
        addressLine1: savedAddress.addressLine1 || "",
        addressLine2: savedAddress.addressLine2 || "",
        city: savedAddress.city || "",
        zipCode: savedAddress.zipCode || "",
        country:
          normalizeCountryStorageValueToIso(savedAddress.country) || "DE",
        email: savedAddress.email || "",
        phoneNumber: savedAddress.phoneNumber || "",
      });
    }
  }, [localStorageKey]);

  const handleChange =
    (field: keyof AddressFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const handleEmailChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      email: value,
    }));
    if (!validateEmail(value)) {
      setErrors((prev) => ({
        ...prev,
        email: t("EmailInvalid"),
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const handlePhoneNumberChange = (phone: string) => {
    setFormData((prev) => ({ ...prev, phoneNumber: phone }));
    if (errors.phoneNumber) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phoneNumber;
        return next;
      });
    }
  };

  const handleCountryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      country: value,
    }));
    if (errors.country) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.country;
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.addressLine1?.trim()) {
      newErrors.addressLine1 = t("FieldRequired");
    } else if (!validateStreetWithHouseNumber(formData.addressLine1)) {
      newErrors.addressLine1 = t("StreetMustIncludeNameAndNumber");
    }
    if (!formData.city?.trim()) {
      newErrors.city = t("FieldRequired");
    }
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = t("FieldRequired");
    }
    if (!formData.country?.trim()) {
      newErrors.country = t("FieldRequired");
    }
    if (!formData.email?.trim()) {
      newErrors.email = t("FieldRequired");
    }
    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = t("FieldRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validate()) {
      return;
    }

    // Save to localStorage
    saveSetupData(localStorageKey, {
      ...formData,
    });

    // Move to next step
    onNext();
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t(titleKey)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(descriptionKey)}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t("Organization") || "Organization"}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {organizationName || t("NoOrganization")}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <CountrySelect
            selector="iso"
            value={formData.country || ""}
            onChange={handleCountryChange}
            required
            error={!!errors.country}
            helperText={errors.country}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 9 }}>
          <AddressStreetAutocomplete
            language={i18n.language}
            country={formData.country || "DE"}
            value={formData.addressLine1 || ""}
            onChange={(street) => {
              setFormData((prev) => ({ ...prev, addressLine1: street }));
              if (errors.addressLine1) {
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.addressLine1;
                  return newErrors;
                });
              }
            }}
            onAddressSelect={(address) => {
              setFormData((prev) => ({
                ...prev,
                addressLine1: address.street || prev.addressLine1,
                zipCode: address.postalCode || prev.zipCode,
                city: address.city || prev.city,
                country:
                  normalizeCountryStorageValueToIso(address.country) ||
                  prev.country,
              }));
              setErrors((prev) => {
                const next = { ...prev };
                delete next.addressLine1;
                delete next.zipCode;
                delete next.city;
                delete next.country;
                return next;
              });
            }}
            label={t("Street")}
            required
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t("AddressLine2") || "Address Line 2"}
            value={formData.addressLine2 || ""}
            onChange={handleChange("addressLine2")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label={t("PostalCode") || "Postal Code"}
            value={formData.zipCode || ""}
            onChange={handleChange("zipCode")}
            required
            error={!!errors.zipCode}
            helperText={errors.zipCode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label={t("City") || "City"}
            value={formData.city || ""}
            onChange={handleChange("city")}
            required
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label={t("Email") || "Email"}
            type="email"
            value={formData.email || ""}
            onChange={(event) => handleEmailChange(event.target.value)}
            required
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PhoneNumberInput
            value={formData.phoneNumber}
            onChange={handlePhoneNumberChange}
            required
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onBack}>
          {t("Back") || "Back"}
        </Button>
        <Button variant="contained" color="primary" onClick={handleNext}>
          {t("Next") || "Next"}
        </Button>
      </Box>
    </Box>
  );
};

export default OrganizationAddressStep;
